/**
 * shopifyApi.js
 *
 * Client-side calls to the quote proxy server (src/quote/api/server.js).
 *
 * Set window.QUOTE_API_BASE in theme.liquid to the server URL, e.g.:
 *   <script>window.QUOTE_API_BASE = 'https://your-proxy.com';</script>
 *
 * In development the Vite dev server proxies /api → localhost:8000 so
 * QUOTE_API_BASE should be left unset (defaults to empty string = same origin).
 *
 * Every function returns { data, errors }.
 * errors is null on success, or an array of human-readable strings on failure.
 */

const API_BASE = () => (typeof window !== 'undefined' ? window.QUOTE_API_BASE ?? '' : '');
const API_SECRET = () => (typeof window !== 'undefined' ? window.QUOTE_API_SECRET ?? '' : '');

function apiHeaders(extra = {}) {
  const headers = { ...extra };
  const secret = API_SECRET();
  if (secret) headers['x-api-secret'] = secret;
  return headers;
}

function supportsXhrProgress() {
  return typeof window !== 'undefined' && typeof XMLHttpRequest !== 'undefined';
}

// async function postMultipartWithProgress({ url, formData, headers = {}, onProgress }) {
//   // If no progress handler (or no XHR), fall back to fetch.
//   if (!onProgress || !supportsXhrProgress()) {
//     const res = await fetch(url, {
//       method: 'POST',
//       headers, // no Content-Type — browser sets it with multipart boundary
//       body: formData,
//     });
//     const json = await res.json().catch(() => ({}));
//     return { ok: res.ok, status: res.status, json };
//   }

//   return await new Promise((resolve) => {
//     const xhr = new XMLHttpRequest();
//     xhr.open('POST', url, true);

//     Object.entries(headers || {}).forEach(([k, v]) => {
//       if (v != null) xhr.setRequestHeader(k, String(v));
//     });

//     xhr.upload.onprogress = (evt) => {
//       if (!evt.lengthComputable) return;
//       const pct = Math.round((evt.loaded / evt.total) * 100);
//       onProgress(pct);
//     };

//     xhr.onload = () => {
//       let json = {};
//       try {
//         json = xhr.responseText ? JSON.parse(xhr.responseText) : {};
//       } catch {}
//       resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, json });
//     };
//     xhr.onerror = () => resolve({ ok: false, status: xhr.status || 0, json: {} });

//     xhr.send(formData);
//   });
// }

// ---------------------------------------------------------------------------
// Customer — find or create
// ---------------------------------------------------------------------------

/**
 * Finds an existing Shopify customer by email, or creates a new one.
 * Returns { data: { customerId, customerGid, isNew, phone }, errors }
 */
export async function createOrFindCustomer({
  firstName,
  lastName,
  email,
  phone,
  company,
  address,
  emailOptIn,
  smsOptIn,
}) {
  const formattedPhone = phone ? `+1${phone.replace(/\D/g, '')}` : undefined;

  const res = await fetch(`${API_BASE()}/api/customer`, {
    method: 'POST',
    headers: apiHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      email,
      firstName,
      lastName,
      phone: formattedPhone,
      address1: address?.address1 ?? '',
      address2: address?.address2 ?? '',
      city: address?.city ?? '',
      state: address?.state ?? '',
      zip: address?.zip ?? '',
      company: company ?? '',
      emailOptIn: !!emailOptIn,
      smsOptIn: !!smsOptIn,
    }),
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok) {
    const msg = json.error || 'Customer creation failed. Please try again.';
    const details = json.details?.map((d) => d.message) ?? [];
    return { data: null, errors: details.length ? details : [msg] };
  }

  return { data: json, errors: null };
}

// ---------------------------------------------------------------------------
// Tax certificate upload
// ---------------------------------------------------------------------------

/**
 * Uploads a tax exemption certificate for a customer.
 * The server handles: stagedUploadsCreate → POST to stage → fileCreate → poll → metafieldsSet.
 *
 * @param {File}   file       — browser File object
 * @param {string} email      — customer email (required)
 * @param {object} [customer] — optional customer fields for create
 *
 * Returns { data: { success, customerId, customerGid, fileGid, fileUrl, urlMetafieldSet }, errors }
 */
export async function uploadTaxCert({ file, email, customer = {} }) {
  const formData = new FormData();
  formData.append('taxCertFile', file, file.name);
  formData.append('email', String(email || ''));
  if (customer?.firstName) formData.append('firstName', String(customer.firstName));
  if (customer?.lastName) formData.append('lastName', String(customer.lastName));
  if (customer?.phone) formData.append('phone', String(customer.phone));
  if (customer?.company) formData.append('company', String(customer.company));
  if (customer?.address1) formData.append('address1', String(customer.address1));
  if (customer?.address2) formData.append('address2', String(customer.address2));
  if (customer?.city) formData.append('city', String(customer.city));
  if (customer?.state) formData.append('state', String(customer.state));
  if (customer?.zip) formData.append('zip', String(customer.zip));

  const res = await fetch(`${API_BASE()}/api/customer/tax-cert`, {
    method: 'POST',
    headers: apiHeaders(), // no Content-Type — browser sets it with multipart boundary
    body: formData,
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok) {
    return {
      data: null,
      errors: [json.error || 'Tax certificate upload failed. Please try again.'],
    };
  }

  return { data: json, errors: null };
}


// ---------------------------------------------------------------------------
// Removal photos upload
// ---------------------------------------------------------------------------

/**
 * Uploads a single removal photo to POST /api/quote/removal-photos.
 * No network request if `file` is empty.
 *
 * Returns { data: { success, fileUrls, file }, errors }
 * - data.file: { fileGid, fileUrl, filename }
 * - fileGid can be passed to submitQuote as oldEquipmentPhotoGid
 *   to set wg_fields.old_equipment_photos (file_reference) on the draft order.
 */
export async function uploadRemovalPhotos({ file, quoteId }) {
  if (!file) return { data: { success: true, fileUrls: [], files: [] }, errors: null };

  const formData = new FormData();
  formData.append('removalPhotosFiles', file, file.name || 'removal-photo');
  if (quoteId) formData.append('quoteId', String(quoteId));

  const res = await fetch(`${API_BASE()}/api/quote/removal-photos`, {
    method: 'POST',
    headers: apiHeaders(),
    body: formData,
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok) {
    return {
      data: null,
      errors: [json.error || 'Removal photos upload failed. Please try again.'],
    };
  }

  return { data: json, errors: null };
}

// ---------------------------------------------------------------------------
// Full quote submission — customer + tax cert metafields + draft order
// ---------------------------------------------------------------------------

/**
 * Submits the full quote to POST /api/quote.
 * The server will:
 *   1. Find or create the customer
 *   2. Set tax cert metafields on the customer (if taxCertFileGid provided)
 *   3. Create the Shopify Draft Order with all line items, attributes & metafields
 *
 * @param {object} formData         - raw form field values
 * @param {Array}  items            - cart line items
 * @param {string} quoteId          - localStorage quote ID
 * @param {string} expirationDate   - ISO date string
 * @param {string|null} taxCertFileGid  - GID from uploadTaxCert (optional)
 * @param {string|null} taxCertFileUrl  - CDN URL from uploadTaxCert (optional)
 * @param {object} analyticsData    - client_id, session_id, gclid, utm_*, etc.
 *
 * Returns { data: { draftOrderId, draftOrderName, invoiceUrl, customerId, isNew }, errors }
 */
export async function submitQuote({
  formData,
  items,
  quoteId,
  expirationDate,
  taxCertFileGid = null,
  taxCertFileUrl = null,
  customerGid = null,
  removalPhotosFileUrl = null,
  removalPhotosFileGid = null, // optional file GID (file_reference)
  analyticsData = {},
  initialLeadScore = null,
  // totalInstallationCost = null,
  // totalInstallationPrice = null,
  // allItemsHaveInstall = null,
}) {
  const res = await fetch(`${API_BASE()}/api/quote`, {
    method: 'POST',
    headers: apiHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      additional_data: { ...formData, ...analyticsData },
      items,
      quoteId,
      expirationDate,
      taxCertFileGid,
      taxCertFileUrl,
      customerGid,
      removalPhotosFileUrl,
      removalPhotosFileGid,
      initialLeadScore,
      analyticsData,
      // totalInstallationCost,
      // totalInstallationPrice,
      // allItemsHaveInstall,
    }),
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok) {
    const msg = json.error || 'Quote submission failed. Please try again.';
    const details = json.details?.map((d) => d.message) ?? [];
    return { data: null, errors: details.length ? details : [msg] };
  }

  return { data: json, errors: null };
}
